import { ProgressItem } from "@features/ax/shared/progressItem";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { Tooltip, TooltipContent, TooltipTrigger } from "@shared/ui/tooltip";
import { HelpCircle } from "lucide-react";
import React from "react";

export const AssetConditionAge: React.FC<{ data: any }> = ({ data }) => {
  const healthDist = data?.assetHealthDistribution;

  const healthStatusData = [
    {
      condition: "Excellent Condition",
      count: `${healthDist?.excellent?.count ?? "NA"} (${
        healthDist?.excellent?.percentage ?? "NA"
      }%)`,
      color: "bg-green-600",
    },
    {
      condition: "Good Condition",
      count: `${healthDist?.good?.count ?? "NA"} (${
        healthDist?.good?.percentage ?? "NA"
      }%)`,
      color: "bg-green-400",
    },
    {
      condition: "Fair Condition",
      count: `${healthDist?.fair?.count ?? "NA"} (${
        healthDist?.fair?.percentage ?? "NA"
      }%)`,
      color: "bg-yellow-400",
    },
    {
      condition: "Poor Condition",
      count: `${healthDist?.poor?.count ?? "NA"} (${
        healthDist?.poor?.percentage ?? "NA"
      }%)`,
      color: "bg-orange-500",
    },
    {
      condition: "Critical Condition",
      count: `${healthDist?.critical?.count ?? "NA"} (${
        healthDist?.critical?.percentage ?? "NA"
      }%)`,
      color: "bg-red-600",
    },
  ];

  // Define colors for different age ranges
  const ageColors = [
    "bg-blue-500", // 0-5 years
    "bg-green-500", // 5-10 years
    "bg-yellow-500", // 10-20 years
    "bg-red-500", // >20 years
  ];

  // Transform asset age profile data with colors and widths
  const transformedAgeProfile = React.useMemo(() => {
    const ageProfile = data?.assetAgeProfile || [];

    // Find the maximum count to calculate relative widths
    const maxCount = Math.max(...ageProfile.map((item) => item.count), 1);

    return ageProfile.map((item, index) => ({
      ...item,
      color: ageColors[index] || "bg-gray-500", // Fallback color
      width: `${(item.count / maxCount) * 100}%`, // Calculate width as percentage
    }));
  }, [data?.assetAgeProfile]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card>
        <CardHeader>
          <div className="flex item-center gap-2">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                Asset Health Status Distribution
              </CardTitle>
            </div>
            <div className="mt-1">
              <Tooltip>
                <TooltipTrigger>
                  <HelpCircle className="h-4 w-4 text-gray-400" />
                </TooltipTrigger>
                <TooltipContent>
                  <p className="max-w-xs">
                    A breakdown of the assets in this system by their current
                    physical condition score.
                  </p>
                </TooltipContent>
              </Tooltip>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3 mt-3">
            {healthStatusData.map((item) => (
              <div
                key={item.condition}
                className="flex items-center justify-between"
              >
                <div className="flex items-center gap-3">
                  <div className={`w-4 h-4 ${item.color} rounded`}></div>
                  <span className="text-sm">{item.condition}</span>
                </div>
                <div className="text-right">
                  <span className="font-semibold">{item.count}</span>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex item-center gap-2">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                Asset Age Profile
              </CardTitle>
            </div>
            <div className="mt-1">
              <Tooltip>
                <TooltipTrigger>
                  <HelpCircle className="h-4 w-4 text-gray-400" />
                </TooltipTrigger>
                <TooltipContent>
                  <p className="max-w-xs">
                    A breakdown of the assets in this system by their age,
                    helping to identify upcoming replacement needs.
                  </p>
                </TooltipContent>
              </Tooltip>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <ProgressItem items={data?.assetAgeProfile} maxValue={100} />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
